import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Field, Form, Input, TextArea, Toggle, Typography } from '@/components';
import { ConnectionInfrastructure, Project } from '@/features/projects/types';
import { formatStringAsInteger, nullableStringSetter, parseStringToInteger } from '@/helpers';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type ConnectionInfraFormFields = {
  evacuation_line_total_distance: number | null;
  evacuation_line_voltage: number | null;
  line_estimated_costs: number | null;
  connection_type: string[];
  connection_to: string[];
  technical_and_economical_conditions_costs: number | null;
  shared_infrastructure: boolean | null;
  shared_infrastructure_comments: string | null;
  connection_costs_comments: string | null;
  connection_infrastructure_comments: string | null;
  gas_grid_connection_pressure: number | null;
  distance_to_gas_injection_point: number | null;
};

export type ConnectionInfraSubmitFields = {
  evacuation_line_total_distance: number | null;
  evacuation_line_voltage: number | null;
  line_estimated_costs: number | null;
  connection_type: string[];
  connection_to: string[];
  technical_and_economical_conditions_costs: number | null;
  shared_infrastructure: boolean | null;
  shared_infrastructure_comments: string | null;
  connection_costs_comments: string | null;
  connection_infrastructure_comments: string | null;
  gas_grid_connection_pressure: number | null;
  distance_to_gas_injection_point: number | null;
};

export type ConnectionInfraSubmitPayload = {
  connection_infrastructure: ConnectionInfraSubmitFields[];
};

export type ConnectionInfraFormNewProps = {
  readonly id: string;
  readonly project: Project;
  readonly onSubmit: (data: ConnectionInfraSubmitPayload) => void;
};

export function ConnectionInfraFormNew({ id, project, onSubmit }: ConnectionInfraFormNewProps) {
  const { t } = useTranslation(['projects']);
  const item = project.connection_infrastructure?.[0] ?? ({} as Partial<ConnectionInfrastructure>);
  const isSolar = project.technologies.some((tech) => tech.collection === 'projects_solar_technologies');
  const isBiogas = project.technologies.some((tech) => tech.collection === 'projects_biogas_technologies');

  const options = useMemo(
    () => ({
      defaultValues: {
        evacuation_line_total_distance: item.evacuation_line_total_distance ?? null,
        evacuation_line_voltage: item.evacuation_line_voltage ?? null,
        line_estimated_costs: item.line_estimated_costs ?? null,
        connection_type: item.connection_type ?? [],
        connection_to: item.connection_to ?? [],
        shared_infrastructure: item.shared_infrastructure ?? false,
        technical_and_economical_conditions_costs: item.technical_and_economical_conditions_costs ?? null,
        shared_infrastructure_comments: item.shared_infrastructure_comments ?? '',
        connection_costs_comments: item.connection_costs_comments ?? '',
        connection_infrastructure_comments: item.connection_infrastructure_comments ?? '',
        gas_grid_connection_pressure: item.gas_grid_connection_pressure ?? null,
        distance_to_gas_injection_point: item.distance_to_gas_injection_point ?? null,
      },
    }),
    [project]
  );

  const onSubmitForm = useCallback(
    (data: ConnectionInfraFormFields) => {
      const payload = {
        evacuation_line_total_distance: data.evacuation_line_total_distance ?? null,
        evacuation_line_voltage: data.evacuation_line_voltage ?? null,
        line_estimated_costs: data.line_estimated_costs ?? null,
        connection_type: data.connection_type ?? null,
        connection_to: data.connection_to ?? null,
        shared_infrastructure: data.shared_infrastructure ?? null,
        technical_and_economical_conditions_costs: data.technical_and_economical_conditions_costs ?? null,
        shared_infrastructure_comments: data.shared_infrastructure_comments ?? '',
        connection_costs_comments: data.connection_costs_comments ?? '',
        connection_infrastructure_comments: data.connection_infrastructure_comments ?? '',
        gas_grid_connection_pressure: data.gas_grid_connection_pressure ?? null,
        distance_to_gas_injection_point: data.distance_to_gas_injection_point ?? null,
      };

      onSubmit({ connection_infrastructure: [payload] });
    },
    [onSubmit, project.id]
  );

  return (
    <Form<ConnectionInfraFormFields> id={id} onSubmit={onSubmitForm} options={options}>
      {({ formState, register, watch }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 italic">
            {t(
              "Please complete this section with detailed information about the project's connection infrastructure, including the line distance, voltage, type (underground/overhead), connection to (substation/power line), expected costs, and any additional comments."
            )}
          </Typography>
          {isBiogas && (
            <>
              <Field
                id="gas_grid_connection_pressure"
                label={t('Gas Grid Connection Pressure')}
                error={formState.errors['gas_grid_connection_pressure']}
                delay={0}
                classes={{ field: 'space-y-1' }}
              >
                <Input
                  id="gas_grid_connection_pressure"
                  type="number"
                  placeholder={t('e.g. 5')}
                  color="primary"
                  suffix="bar"
                  registration={register('gas_grid_connection_pressure', { ...NUMERIC })}
                />
              </Field>

              <Field
                id="distance_to_gas_injection_point"
                label={t('Distance to Gas Injection Point')}
                error={formState.errors['distance_to_gas_injection_point']}
                delay={0}
                classes={{ field: 'space-y-1' }}
              >
                <Input
                  id="distance_to_gas_injection_point"
                  type="number"
                  placeholder={t('e.g. 10')}
                  color="primary"
                  suffix="km"
                  registration={register('distance_to_gas_injection_point', { ...NUMERIC })}
                />
              </Field>
            </>
          )}
          {!isBiogas && (
            <>
              <Field
                id="evacuation_line_total_distance"
                label={t('Evacuation line distance')}
                error={formState.errors['evacuation_line_total_distance']}
                delay={0}
                classes={{ field: 'space-y-1' }}
              >
                <Input
                  id="evacuation_line_total_distance"
                  type="number"
                  placeholder={t('e.g. 5')}
                  color="primary"
                  suffix="km"
                  registration={register('evacuation_line_total_distance', { ...NUMERIC })}
                />
              </Field>

              <Field
                id="evacuation_line_voltage"
                label={t('Evacuation line voltage')}
                error={formState.errors['evacuation_line_voltage']}
                delay={0}
                classes={{ field: 'space-y-1' }}
              >
                <Input
                  id="evacuation_line_voltage"
                  type="number"
                  placeholder={t('e.g. 5')}
                  color="primary"
                  suffix="kV"
                  registration={register('evacuation_line_voltage', { ...NUMERIC })}
                />
              </Field>

              <Field label={t('Connection type')} delay={0} classes={{ field: 'space-y-2', input: 'flex space-x-3' }}>
                <Checkbox color="primary" registration={register('connection_type')} value="underground">
                  {t('Underground')}
                </Checkbox>
                <Checkbox color="primary" registration={register('connection_type')} value="aerial">
                  {t('Aerial')}
                </Checkbox>
              </Field>

              <Field label={t('Connection to')} delay={0} classes={{ field: 'space-y-2', input: 'flex space-x-3' }}>
                <Checkbox color="primary" registration={register('connection_to')} value="powerline">
                  {t('Power line')}
                </Checkbox>
                <Checkbox color="primary" registration={register('connection_to')} value="substation">
                  {t('Substation')}
                </Checkbox>
              </Field>
            </>
          )}

          <Field
            id="line_estimated_costs"
            label={t('Line estimated costs')}
            error={formState.errors['line_estimated_costs']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="line_estimated_costs"
              type="number"
              placeholder={t('e.g. 150.000')}
              color="primary"
              suffix={isSolar ? '€/MWp' : '€/MW'}
              registration={register('line_estimated_costs', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="technical_and_economical_conditions_costs"
            label={t('Grid Conditions Costs')}
            error={formState.errors['technical_and_economical_conditions_costs']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="technical_and_economical_conditions_costs"
              type="number"
              placeholder={t('e.g. 150.000')}
              color="primary"
              suffix={isSolar ? '€/MWp' : '€/MW'}
              registration={register('technical_and_economical_conditions_costs', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="connection_costs_comments"
            label={t('Connection Costs Comments')}
            error={formState.errors['connection_costs_comments']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="connection_costs_comments"
              color="primary"
              registration={register('connection_costs_comments', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="shared_infrastructure"
            label={t('Shared Infrastructure')}
            error={formState.errors['shared_infrastructure']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Toggle id="shared_infrastructure" registration={register('shared_infrastructure')} />
          </Field>

          {watch('shared_infrastructure') && (
            <Field
              id="shared_infrastructure_comments"
              label={t('Shared Infrastructure Comments')}
              error={formState.errors['shared_infrastructure_comments']}
              delay={0}
              classes={{ field: 'space-y-1' }}
            >
              <TextArea
                id="shared_infrastructure_comments"
                color="primary"
                registration={register('shared_infrastructure_comments', { setValueAs: nullableStringSetter })}
              />
            </Field>
          )}
          <Field
            id="connection_infrastructure_comments"
            label={t('Other Connection Infrastructure Comments')}
            error={formState.errors['connection_infrastructure_comments']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="connection_infrastructure_comments"
              color="primary"
              registration={register('connection_infrastructure_comments', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
